// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayJs from 'dayjs';
import dayJsRecur from 'dayjs-recur';
import { RECURRING_FREQUENCY_UNITS } from './types';

export interface FrequencyGroup {
  readonly frequencyType: string;
  readonly frequencies: readonly number[];
}
export interface RecurringOptions {
  readonly frequencyGroups: readonly FrequencyGroup[];
  readonly exceptions: readonly string[];
}

export const parseRecurrenceOptions = (options: string): RecurringOptions => {
  const parsedOptions = JSON.parse(options);
  if (!Array.isArray(parsedOptions.exceptions)) {
    throw new Error('Invalid recurrence options. Missing exceptions');
  }
  if (!Array.isArray(parsedOptions.frequencyGroups)) {
    throw new Error('Invalid recurrence options. Frequencies must be an array');
  }
  return {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    exceptions: parsedOptions.exceptions.map((exception: any) => {
      if (typeof exception !== 'string') {
        throw new Error('Invalid recurrence options. exception must be a string');
      }
      return exception;
    }),
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    frequencyGroups: parsedOptions.frequencyGroups.map((option: any) => {
      // Check if option is not an object
      if (typeof option !== 'object') {
        throw new Error('Invalid recurrence options. Must be an array of objects');
      }
      if (!option.frequencyType || !option.frequencies) {
        throw new Error('Invalid recurrence options. Missing frequencyType or frequency');
      }
      // Check that frequencyType is a string and frequencies is a number array
      if (typeof option.frequencyType !== 'string') {
        throw new Error('Invalid recurrence options. frequencyType must be a string');
      }
      if (!Array.isArray(option.frequencies)) {
        throw new Error('Invalid recurrence options. frequencies must be an array');
      }
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const frequencies = (option.frequencies as any[]).map((frequency) => {
        if (typeof frequency !== 'number') {
          throw new Error('Invalid recurrence options. frequency must be a number');
        }
        return frequency;
      });
      return {
        frequencyType: option.frequencyType as string,
        frequencies,
      };
    }),
  };
};

export function addTwelveHours(date: dayJs.Dayjs) {
  return date.add(12, 'hour');
}
export function addTwelveHoursNullable(date: dayJs.Dayjs | null) {
  return date?.add(12, 'hour') ?? null;
}
export function subtractTwelveHours(date: dayJs.Dayjs) {
  return date.subtract(12, 'hour');
}
export function subtractTwelveHoursNullable(date: dayJs.Dayjs | null) {
  return date?.subtract(12, 'hour') ?? null;
}

export const createRecurrence = ({
  startTime,
  endTime,
  options,
  shouldNotApplyExceptions,
}: {
  startTime: number;
  endTime?: number;
  options: RecurringOptions;
  shouldNotApplyExceptions?: boolean;
}) => {
  dayJs.extend(dayJsRecur);

  // Safeguard to make sure start time is never later then end time
  // Otherwise recur will throw an error
  let safeEndTime = endTime;
  if ((endTime !== undefined && startTime > endTime) || Number.isNaN(endTime)) {
    safeEndTime = startTime;
  }
  const start = dayJs.unix(startTime);
  const end = safeEndTime ? dayJs.unix(safeEndTime) : undefined;
  const recurrence = dayJs.recur({
    start,
    end,
  });
  for (const option of options.frequencyGroups) {
    // Each option has frequencyType (string) and frequency (int/number)
    // Unnecessary maps are just for TypeScript
    if (option.frequencyType === RECURRING_FREQUENCY_UNITS.DAYS) {
      recurrence.every(option.frequencies.map((f) => f)).days();
    }
    if (option.frequencyType === RECURRING_FREQUENCY_UNITS.WEEKS) {
      recurrence.every(option.frequencies.map((f) => f)).weeks();
    }
    if (option.frequencyType === RECURRING_FREQUENCY_UNITS.DAY_OF_WEEK) {
      recurrence.every(option.frequencies.map((f) => f)).daysOfWeek();
    }
    if (option.frequencyType === RECURRING_FREQUENCY_UNITS.DAY_OF_MONTH) {
      recurrence.every(option.frequencies.map((f) => f)).daysOfMonth();
    }
    if (option.frequencyType === RECURRING_FREQUENCY_UNITS.NTH_WEEKDAY_OF_MONTH) {
      // This is weird. To fit the existing format, we'll have to parse the array of frequencies very carefully
      // The first element is the target weekday
      // The second element is the nth occurrence of that weekday per month
      // Ex: [0, 1] would mean second Monday of every month
      const [targetWeekday, nthOccurrence] = option.frequencies;
      recurrence.every(targetWeekday).daysOfWeek().every([nthOccurrence]).weeksOfMonthByDay();
    }
  }
  if (!shouldNotApplyExceptions) {
    for (const exception of options.exceptions) {
      recurrence.except(exception);
    }
  }

  return recurrence;
};

export const dayOfWeekToNumber = (dayOfWeek: string) => {
  const dayOfWeekLower = dayOfWeek.toLowerCase();
  switch (dayOfWeekLower) {
    case 'sunday':
    case 'sun':
      return 0;
    case 'monday':
    case 'mon':
      return 1;
    case 'tuesday':
    case 'tue':
      return 2;
    case 'wednesday':
    case 'wed':
      return 3;
    case 'thursday':
    case 'thu':
      return 4;
    case 'friday':
    case 'fri':
      return 5;
    case 'saturday':
    case 'sat':
      return 6;
    default:
      throw new Error('Invalid day of week');
  }
};

export const numberToDayOfWeek = (dayOfWeek: number, shorten = false) => {
  switch (dayOfWeek) {
    case 0:
      return shorten ? 'Sun' : 'Sunday';
    case 1:
      return shorten ? 'Mon' : 'Monday';
    case 2:
      return shorten ? 'Tue' : 'Tuesday';
    case 3:
      return shorten ? 'Wed' : 'Wednesday';
    case 4:
      return shorten ? 'Thu' : 'Thursday';
    case 5:
      return shorten ? 'Fri' : 'Friday';
    case 6:
      return shorten ? 'Sat' : 'Saturday';
    default:
      throw new Error('Invalid day of week number');
  }
};
